import { KeyboardEvent, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { COMPANY_AREAS, CompanyAreaId, formatCompanyArea, isCompanyAreaId } from '../../types/CompanyArea';

type TaskAreaPickerProps = {
  selectedIds: string[];
  onChange: (ids: CompanyAreaId[]) => void;
  disabled?: boolean;
};

type MenuPosition = {
  top: number;
  left: number;
  width: number;
  maxHeight: number;
};

export function TaskAreaPicker({
  selectedIds,
  onChange,
  disabled = false,
}: TaskAreaPickerProps) {
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [menuPosition, setMenuPosition] = useState<MenuPosition>({
    top: 0,
    left: 0,
    width: 280,
    maxHeight: 240,
  });

  const selected = useMemo(
    () => selectedIds.filter(isCompanyAreaId),
    [selectedIds],
  );

  const options = useMemo(() => {
    const term = query.trim().toLowerCase();
    return COMPANY_AREAS.filter((area) => !selected.includes(area.id))
      .filter((area) => !term || area.label.toLowerCase().includes(term));
  }, [query, selected]);

  function updateMenuPosition(): void {
    const rect = inputRef.current?.getBoundingClientRect();
    if (!rect) return;
    const width = Math.max(rect.width, 220);
    const maxLeft = window.innerWidth - width - 8;
    const gap = 4;
    const padding = 8;
    const spaceBelow = window.innerHeight - rect.bottom - padding;
    const spaceAbove = rect.top - padding;
    const openUpward = spaceBelow < 140 && spaceAbove > spaceBelow;
    const available = Math.max(96, (openUpward ? spaceAbove : spaceBelow) - gap);
    const maxHeight = Math.min(240, available);
    setMenuPosition({
      top: openUpward ? rect.top - gap - maxHeight : rect.bottom + gap,
      left: Math.max(8, Math.min(rect.left, maxLeft)),
      width,
      maxHeight,
    });
  }

  function add(id: CompanyAreaId): void {
    if (disabled || selected.includes(id)) return;
    onChange([...selected, id]);
    setQuery('');
    setOpen(false);
  }

  function remove(id: CompanyAreaId): void {
    if (disabled) return;
    onChange(selected.filter((item) => item !== id));
  }

  function commitQuery(): void {
    const term = query.trim().toLowerCase();
    setOpen(false);
    setQuery('');
    if (!term) return;
    const exact = options.filter((area) => area.label.toLowerCase() === term);
    const match = exact.length === 1 ? exact[0] : options.length === 1 ? options[0] : null;
    if (match) add(match.id);
  }

  useEffect(() => {
    setHighlightedIndex(0);
  }, [query, options.length]);

  useEffect(() => {
    if (!open) return;

    updateMenuPosition();

    function handlePointerDown(event: PointerEvent): void {
      const target = event.target as Node | null;
      if (rootRef.current?.contains(target)) return;
      const menu = document.getElementById(listId);
      if (menu?.contains(target)) return;
      commitQuery();
    }

    function handleReposition(): void {
      updateMenuPosition();
    }

    window.addEventListener('pointerdown', handlePointerDown);
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);
    return () => {
      window.removeEventListener('pointerdown', handlePointerDown);
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [open, listId, query, options, selected, disabled]);

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      setHighlightedIndex((current) => Math.min(current + 1, Math.max(options.length - 1, 0)));
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setOpen(true);
      setHighlightedIndex((current) => Math.max(current - 1, 0));
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      const area = options[highlightedIndex];
      if (area) add(area.id);
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
      setQuery('');
    }
  }

  return (
    <div ref={rootRef} className="space-y-2">
      <label className="block space-y-1">
        <span className="text-sm font-medium text-slate-700">Áreas</span>
        <input
          ref={inputRef}
          className="erp-input w-full"
          value={query}
          disabled={disabled}
          placeholder="Digite a área"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          onFocus={() => {
            if (!disabled) setOpen(true);
          }}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onKeyDown={handleKeyDown}
        />
      </label>

      {selected.length > 0 ? (
        <ul className="flex flex-wrap gap-2">
          {selected.map((id) => (
            <li key={id}>
              <span className="inline-flex items-center gap-1 rounded-full border border-secondary-200 bg-secondary-50 py-1 pl-2.5 pr-1 text-sm text-secondary-800">
                {formatCompanyArea(id)}
                <button
                  type="button"
                  className="inline-flex h-5 w-5 items-center justify-center rounded-full text-secondary-500 hover:bg-secondary-200 hover:text-secondary-900 disabled:cursor-not-allowed disabled:opacity-50"
                  aria-label={`Remover ${formatCompanyArea(id)}`}
                  disabled={disabled}
                  onClick={() => remove(id)}
                >
                  ×
                </button>
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      {open && !disabled
        ? createPortal(
            <ul
              id={listId}
              role="listbox"
              className="fixed z-[120] overflow-y-auto rounded-lg border border-secondary-200 bg-white py-1 shadow-xl"
              style={{
                top: menuPosition.top,
                left: menuPosition.left,
                width: menuPosition.width,
                maxHeight: menuPosition.maxHeight,
              }}
            >
              {options.length === 0 ? (
                <li className="px-3 py-2 text-sm text-secondary-500">
                  {selected.length === COMPANY_AREAS.length
                    ? 'Todas as áreas já foram vinculadas.'
                    : 'Nenhuma área encontrada.'}
                </li>
              ) : (
                options.map((area, index) => (
                  <li key={area.id} role="option" aria-selected={index === highlightedIndex}>
                    <button
                      type="button"
                      className={`w-full px-3 py-2 text-left text-sm ${
                        index === highlightedIndex ? 'bg-secondary-100' : 'hover:bg-secondary-50'
                      }`}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      onPointerDown={(event) => event.preventDefault()}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => add(area.id)}
                    >
                      {area.label}
                    </button>
                  </li>
                ))
              )}
            </ul>,
            document.body,
          )
        : null}
    </div>
  );
}
